import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import { FaArrowLeft } from "react-icons/fa";
import "../../src/css/ActivityPage.css";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaChevronDown,
  FaUserGraduate,
  FaLayerGroup,
  FaClipboardList,
  FaExclamationTriangle,
} from "react-icons/fa";
import { toast } from "react-toastify";

const ActivityPage = () => {
  const { search, state } = useLocation();
  const params = new URLSearchParams(search);
  const subject_id = params.get("subject_id");
  const navigate = useNavigate();

  const [activityTypes, setActivityTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const [studentScores, setStudentScores] = useState({});
  const [loadingStudentScores, setLoadingStudentScores] = useState(false);
  // Loading state for initial fetch
  const [loadingFetch, setLoadingFetch] = useState(false);

  // Editing states
  const [editingScore, setEditingScore] = useState(null);
  // const [editingScoreId, setEditingScoreId] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  // Deleting states
  const [deletingTypeId, setDeletingTypeId] = useState(null);
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [deletingScoreId, setDeletingScoreId] = useState(null);

  // Create Activity Type
  const [newType, setNewType] = useState({
    name: "",
    weight: "",
  });
  const [loadingType, setLoadingType] = useState(false);

  // Create Activity Item
  const [newItem, setNewItem] = useState({
    activity_type_id: "",
    title: "",
    total_mark: "",
  });
  const [loadingItem, setLoadingItem] = useState(false);

  // Create Score
  const [newScore, setNewScore] = useState({
    activity_item_id: "",
    score: "",
  });
  const [loadingScore, setLoadingScore] = useState(false);

  const token = useMemo(() => {
    const tokenString = localStorage.getItem("authToken");
    return tokenString ? JSON.parse(tokenString) : null;
  }, []);

  const authHeader = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token?.access}`,
      },
    }),
    [token]
  );

  // 🔹 Filter students who take this subject
  const subjectStudents = useMemo(() => {
    const students = state?.students || [];

    return students.filter((student) =>
      student.subjects.some((sub) => sub.id === Number(subject_id))
    );
  }, [state?.students, subject_id]);

  const subjectName =
    state.students[0]?.subjects?.find((sub) => sub.id === Number(subject_id))
      ?.name || "";


  // 🔹 Fetch Activity Types + Items (Subject-level only)

  const fetchSubjectData = useCallback(async () => {
    setLoadingFetch(true);

    try {
      // First API call → permission check
      const resTypes = await axios.get(
        `${API_URL}/subject-activity-types/${subject_id}/`,
        authHeader
      );

      if (!resTypes || resTypes.status === 403 || resTypes.status === 404) {
        toast.error("You are not permitted to access this subject.");
        navigate(-1);
        return;
      }

      const types = resTypes.data;
      setActivityTypes(types);

      // Fetch all activity items
      const allItems = [];

      for (const type of types) {
        const resItems = await axios.get(
          `${API_URL}/activity-items/${type.id}/`,
          authHeader
        );

        const typedItems = resItems.data.map((item) => ({
          ...item,
          activity_type: type.id,
        }));

        allItems.push(...typedItems);
      }

      setItems(allItems);
    } catch (error) {
      console.error("Error fetching subject data:", error);

      if (
        error.response &&
        (error.response.status === 403 || error.response.status === 404)
      ) {
        toast.error("You are not permitted to access this subject.");
        navigate(-1);
      } else {
        toast.error("Something went wrong. Please try again later.");
      }
    } finally {
      setLoadingFetch(false);
    }
  }, [subject_id, authHeader, navigate]);

  const fetchAllStudentScores = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/student-scores/subject/${subject_id}/all/`,
        authHeader
      );

      setStudentScores(res.data);
    } catch (error) {
      console.error("Error loading scores:", error);
    }
  }, [subject_id, authHeader]);

  useEffect(() => {
    fetchSubjectData();
    fetchAllStudentScores();
  }, [fetchSubjectData, fetchAllStudentScores]);

  const handleCreateType = async () => {
    if (!newType.name || !newType.weight) return;

    setLoadingType(true);

    try {
      if (editingType) {
        //  setEditingId(editingType.id);
        // UPDATE
        await axios.put(
          `${API_URL}/subject-activity-types/${subject_id}/${editingType.id}/`,
          newType,
          authHeader
        );

        await fetchSubjectData();
        setEditingType(null);
        toast.success("activity type updated successfully");
      } else {
        // CREATE
        const res = await axios.post(
          `${API_URL}/subject-activity-types/${subject_id}/`,
          newType,
          authHeader
        );

        setActivityTypes((prev) => [...prev, res.data]);
      }
      toast.success("activity type created successfully");
      setNewType({ name: "", weight: "" });
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingType(false);
    }
  };

  const handleCreateScore = async (studentId) => {
    if (!newScore.activity_item_id || !newScore.score) return;

    setLoadingScore(true);

    try {
      if (editingScore) {
        // ✏️ UPDATE SCORE
        await axios.put(
          `${API_URL}/student-scores/student/${studentId}/${editingScore.id}/`,
          { score: Number(newScore.score) },
          authHeader
        );

        setStudentScores((prev) => ({
          ...prev,
          [studentId]: prev[studentId].map((s) =>
            s.id === editingScore.id
              ? { ...s, score: Number(newScore.score) }
              : s
          ),
        }));

        toast.success("Score updated successfully");
      } else {
        // ➕ CREATE SCORE
        const payload = {
          inststudent: studentId,
          activity_item: Number(newScore.activity_item_id),
          score: Number(newScore.score),
        };

        const res = await axios.post(
          `${API_URL}/student-scores/student/${studentId}/`,
          payload,
          authHeader
        );

        setStudentScores((prev) => ({
          ...prev,
          [studentId]: [...(prev[studentId] || []), res.data],
        }));

        toast.success("Score added successfully");
      }

      // reset form
      setNewScore({ activity_item_id: "", score: "" });
      setEditingScore(null);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setLoadingScore(false);
    }
  };

  // 🔹 Fetch Scores When Student Expands
  const handleToggleStudent = async (studentId) => {
    if (expandedStudentId === studentId) {
      setExpandedStudentId(null);
      return;
    }

    setExpandedStudentId(studentId);

    // If scores already fetched, don't fetch again
    if (studentScores[studentId]) return;

    setLoadingStudentScores(true);

    try {
      const res = await axios.get(
        `${API_URL}/student-scores/subject/${subject_id}/student/${studentId}/`,
        authHeader
      );

      setStudentScores((prev) => ({
        ...prev,
        [studentId]: res.data,
      }));
    } catch (error) {
      console.error(
        "Error fetching student scores:",
        error.response?.data || error.message
      );

      if (error.response?.status === 500) {
        alert("Server error while loading scores. Backend needs to fix this.");
      }
    } finally {
      setLoadingStudentScores(false);
    }
  };

  const handleCreateItem = async () => {
    if (!newItem.activity_type_id || !newItem.title || !newItem.total_mark)
      return;

    setLoadingItem(true);

    try {
      if (editingItem) {
        // UPDATE ITEM
        await axios.put(
          `${API_URL}/activity-items/${editingItem.activity_type}/${editingItem.id}/`,
          {
            title: newItem.title,
            total_mark: newItem.total_mark,
          },
          authHeader
        );

        await fetchSubjectData();
        toast.success("item updated successfully");
        setEditingItem(null);
      } else {
        // CREATE ITEM
        const res = await axios.post(
          `${API_URL}/activity-items/${newItem.activity_type_id}/`,
          {
            title: newItem.title,
            total_mark: newItem.total_mark,
          },
          authHeader
        );

        setItems((prev) => [
          ...prev,
          { ...res.data, activity_type: Number(newItem.activity_type_id) },
        ]);
      }

      // Reset form
      setNewItem({
        activity_type_id: "",
        title: "",
        total_mark: "",
      });
      toast.success("item created successfully");
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingItem(false);
    }
  };

  const getScore = (studentId, itemId) => {
    const scores = studentScores[studentId] || [];
    const scoreObj = scores.find((s) => s.activity_item === itemId);
    return scoreObj ? scoreObj.score : "N/A";
  };

  const handleEditType = (type) => {
    setEditingType(type);
    setNewType({
      name: type.name,
      weight: type.weight,
    });
  };

  const handleDeleteType = async (typeId) => {
    setDeletingTypeId(typeId);
    try {
      await axios.delete(
        `${API_URL}/subject-activity-types/${subject_id}/${typeId}/`,
        {
          ...authHeader,
        }
      );
      await fetchSubjectData();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Delete failed");
    }
    setDeletingTypeId(null);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);

    setNewItem({
      activity_type_id: item.activity_type,
      title: item.title,
      total_mark: item.total_mark,
    });
  };

  const handleDeleteItem = async (itemId) => {
    setDeletingItemId(itemId);
    const item = items.find((i) => i.id === itemId);
    try {
      await axios.delete(
        `${API_URL}/activity-items/${item.activity_type}/${itemId}/`,
        {
          ...authHeader,
        }
      );
      await fetchSubjectData();
    } catch (err) {
      console.error(err);
    }
    setDeletingItemId(null);
  };

  const handleEditScore = (itemId, studentId) => {
    const scores = studentScores[studentId] || [];
    const currentScore = scores.find((s) => s.activity_item === itemId);

    if (!currentScore) return;

    setEditingScore({
      id: currentScore.id,
      studentId,
    });

    setNewScore({
      activity_item_id: currentScore.activity_item,
      score: currentScore.score,
    });
  };

  const handleDeleteScore = async (itemId, studentId) => {
    setDeletingScoreId(itemId);
    const scores = studentScores[studentId] || [];
    const currentScore = scores.find((s) => s.activity_item === itemId);
    try {
      await axios.delete(
        `${API_URL}/student-scores/student/${studentId}/${currentScore.id}/`,
        {
          ...authHeader,
        }
      );
      await fetchSubjectData();

      // ✅ Remove from state
      setStudentScores((prev) => ({
        ...prev,
        [studentId]: prev[studentId].filter((s) => s.id !== currentScore.id),
      }));
      toast.success("score deleted successfully");
    } catch (err) {
      console.error(err);
    }
    setDeletingScoreId(null);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      color: "#0c1a2e",
      borderRadius: 2,
      background: "#fff",
      "& fieldset": { borderColor: "rgba(2,136,209,0.25)" },
      "&:hover fieldset": { borderColor: "#0288D1" },
      "&.Mui-focused fieldset": { borderColor: "#0288D1" },
    },
    "& input::placeholder": { color: "#94a3b8" },
  };

  const selectSx = {
    color: "#0c1a2e",
    borderRadius: 2,
    background: "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(2,136,209,0.25)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#0288D1" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#0288D1",
    },
    "& .MuiSvgIcon-root": { color: "#0288D1" },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.82rem",
    background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
    boxShadow: "0 4px 12px rgba(2,136,209,0.25)",
    "&:hover": { background: "linear-gradient(135deg, #0277bd, #0288D1)" },
    "&:disabled": { opacity: 0.5 },
  };

  const editIconSx = {
    color: "#0288D1",
    background: "rgba(2,136,209,0.08)",
    border: "1px solid rgba(2,136,209,0.2)",
    borderRadius: 1.5,
    "&:hover": { background: "rgba(2,136,209,0.16)" },
  };

  const deleteIconSx = {
    color: "#ef4444",
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.2)",
    borderRadius: 1.5,
    "&:hover": { background: "rgba(239,68,68,0.16)" },
  };
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 50%, #e1f5fe 100%)",
        py: 4,
        px: { xs: 2, md: 4 },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 3,
          background: "#ffffff",
          border: "1px solid rgba(2,136,209,0.15)",
          boxShadow: "0 2px 12px rgba(2,136,209,0.08)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: "#0288D1",
              background: "rgba(2,136,209,0.08)",
              border: "1px solid rgba(2,136,209,0.2)",
              "&:hover": { background: "rgba(2,136,209,0.15)" },
            }}
          >
            <FaArrowLeft size={16} />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 900,
                color: "#0c1a2e",
                letterSpacing: "-0.5px",
                lineHeight: 1.2,
              }}
            >
              Assessment Setup
            </Typography>

            <Typography
              sx={{
                fontSize: "0.9rem",
                fontWeight: 700,
                color: "#0288D1",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                mt: 0.5,
              }}
            >
              {subjectName}
            </Typography>

            <Typography
              sx={{
                fontSize: "0.85rem",
                color: "#64748b",
                mt: 0.5,
              }}
            >
              Setup assessment category, assessment component and students scores obtained
            </Typography>
          </Box>
          {loadingFetch && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={18} sx={{ color: "#0288D1" }} />
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Loading...
              </Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        {/* ── LEFT COLUMN ─────────────────────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            {/* ── Activity Types Card ───────────────────────────────────────── */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                background: "#fff",
                border: "1px solid rgba(2,136,209,0.15)",
                boxShadow: "0 2px 12px rgba(2,136,209,0.08)",
                overflow: "hidden",
              }}
            >
              {/* Card Header */}
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  borderBottom: "1px solid #e2e8f0",
                  background:
                    "linear-gradient(135deg, rgba(2,136,209,0.05), rgba(14,165,233,0.03))",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 4px 10px rgba(2,136,209,0.3)",
                    }}
                  >
                    <FaLayerGroup size={14} color="#fff" />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: "#0c1a2e",
                        fontSize: "0.92rem",
                      }}
                    >
                      Assessment Category
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      {activityTypes.length} categories · total weight{" "}
                      {activityTypes.reduce((s, t) => s + Number(t.weight), 0)}%
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ p: 2.5 }}>
                {/* Add form */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    background: "rgba(2,136,209,0.04)",
                    border: "1px solid rgba(2,136,209,0.14)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      color: "#0288D1",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      mb: 1.5,
                    }}
                  >
                    {editingType ? "✏️ Edit category" : "➕ New category"}
                  </Typography>
                  <Stack spacing={1.5}>
                    <TextField
                      placeholder="e.g. Exam, Test, Assignment, Project"
                      value={newType.name}
                      onChange={(e) =>
                        setNewType({ ...newType, name: e.target.value })
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                    />
                    <TextField
                      placeholder="Weight %, e.g. 60, 40, 20"
                      type="number"
                      value={newType.weight}
                      onChange={(e) =>
                        setNewType({ ...newType, weight: e.target.value })
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                    />
                    <Button
                      onClick={handleCreateType}
                      disabled={loadingType}
                      variant="contained"
                      fullWidth
                      startIcon={loadingType ? null : <FaPlus size={11} />}
                      sx={primaryBtnSx}
                    >
                      {loadingType ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : editingType ? (
                        "Update category"
                      ) : (
                        "Add category"
                      )}
                    </Button>
                    {editingType && (
                      <Button
                        size="small"
                        onClick={() => {
                          setEditingType(null);
                          setNewType({ name: "", weight: "" });
                        }}
                        sx={{ mt: 1 }}
                      >
                        Cancel Edit
                      </Button>
                    )}
                  </Stack>
                </Paper>

                {/* Type list */}
                <Stack spacing={1}>
                  {activityTypes.map((type) => (
                    <Paper
                      key={type.id}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "rgba(2,136,209,0.35)",
                          boxShadow: "0 2px 8px rgba(2,136,209,0.08)",
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: "#0c1a2e",
                              fontSize: "0.85rem",
                            }}
                          >
                            {type.name}
                          </Typography>
                          <Chip
                            label={`${type.weight}%`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              background:
                                Number(type.weight) === 0
                                  ? "rgba(239,68,68,0.1)"
                                  : "rgba(2,136,209,0.1)",
                              color:
                                Number(type.weight) === 0
                                  ? "#dc2626"
                                  : "#0288D1",
                              border: "none",
                              mt: 0.3,
                            }}
                            icon={
                              Number(type.weight) === 0 ? (
                                <FaExclamationTriangle
                                  size={8}
                                  color="#dc2626"
                                  style={{ marginLeft: 6 }}
                                />
                              ) : undefined
                            }
                          />
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleEditType(type)}
                              disabled={loadingType}
                              sx={editIconSx}
                            >
                              <FaEdit size={12} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteType(type.id)}
                              disabled={deletingTypeId === type.id}
                              sx={deleteIconSx}
                            >
                              {deletingTypeId === type.id ? (
                                <CircularProgress
                                  size={12}
                                  sx={{ color: "#ef4444" }}
                                />
                              ) : (
                                <FaTrash size={12} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </Paper>

            {/* ── Activity Items Card ───────────────────────────────────────── */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                background: "#fff",
                border: "1px solid rgba(2,136,209,0.15)",
                boxShadow: "0 2px 12px rgba(2,136,209,0.08)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  borderBottom: "1px solid #e2e8f0",
                  background:
                    "linear-gradient(135deg, rgba(2,136,209,0.05), rgba(14,165,233,0.03))",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 4px 10px rgba(124,58,237,0.3)",
                    }}
                  >
                    <FaClipboardList size={14} color="#fff" />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: "#0c1a2e",
                        fontSize: "0.92rem",
                      }}
                    >
                      Assessment Component
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      {items.length} Component created
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ p: 2.5 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    background: "rgba(124,58,237,0.04)",
                    border: "1px solid rgba(124,58,237,0.14)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      color: "#7c3aed",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      mb: 1.5,
                    }}
                  >
                    ➕ New Component
                  </Typography>
                  <Stack spacing={1.5}>
                    <Select
                      value={newItem.activity_type_id}
                      onChange={(e) =>
                        setNewItem({
                          ...newItem,
                          activity_type_id: e.target.value,
                        })
                      }
                      displayEmpty
                      size="small"
                      fullWidth
                      sx={selectSx}
                    >
                      <MenuItem value="" disabled sx={{ color: "#94a3b8" }}>
                        Select Assessment Category
                      </MenuItem>
                      {activityTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <TextField
                      placeholder="e.g. Objective, Theory, Practical, Test 1, Assignment"
                      value={newItem.title}
                      onChange={(e) =>
                        setNewItem({ ...newItem, title: e.target.value })
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                    />
                    <TextField
                      placeholder="Maximum Marks, e.g. 60, 40, 20"
                      type="number"
                      value={newItem.total_mark}
                      onChange={(e) =>
                        setNewItem({ ...newItem, total_mark: e.target.value })
                      }
                      size="small"
                      fullWidth
                      sx={inputSx}
                    />
                    <Button
                      onClick={handleCreateItem}
                      disabled={loadingItem}
                      variant="contained"
                      fullWidth
                      startIcon={loadingItem ? null : <FaPlus size={11} />}
                      sx={{
                        ...primaryBtnSx,
                        background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                        boxShadow: "0 4px 12px rgba(124,58,237,0.3)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #6d28d9, #7c3aed)",
                        },
                      }}
                    >
                      {loadingItem ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : editingItem ? (
                        "Update Component"
                      ) : (
                        "Add Component"
                      )}
                    </Button>
                    {editingItem && (
                      <Button
                        onClick={() => {
                          setEditingItem(null);
                          setNewItem({
                            activity_type_id: "",
                            title: "",
                            total_mark: "",
                          });
                        }}
                        size="small"
                        sx={{ mt: 1 }}
                      >
                        Cancel Edit
                      </Button>
                    )}
                  </Stack>
                </Paper>

                <Stack spacing={1}>
                  {items.map((item) => (
                    <Paper
                      key={item.id}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "rgba(124,58,237,0.3)",
                          boxShadow: "0 2px 8px rgba(124,58,237,0.07)",
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: "#0c1a2e",
                              fontSize: "0.85rem",
                            }}
                          >
                            {item.title}
                          </Typography>
                          <Chip
                            label={`${item.total_mark} pts`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              background: "rgba(124,58,237,0.1)",
                              color: "#7c3aed",
                              border: "none",
                              mt: 0.3,
                            }}
                          />
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleEditItem(item)}
                              disabled={loadingItem}
                              sx={editIconSx}
                            >
                              <FaEdit size={12} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteItem(item.id)}
                              disabled={deletingItemId === item.id}
                              sx={deleteIconSx}
                            >
                              {deletingItemId === item.id ? (
                                <CircularProgress
                                  size={12}
                                  sx={{ color: "#ef4444" }}
                                />
                              ) : (
                                <FaTrash size={12} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </Paper>
          </Stack>
        </Grid>

        {/* ── RIGHT COLUMN — STUDENTS ──────────────────────────────────────── */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              background: "#fff",
              border: "1px solid rgba(2,136,209,0.15)",
              boxShadow: "0 2px 12px rgba(2,136,209,0.08)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 2.5,
                py: 2,
                borderBottom: "1px solid #e2e8f0",
                background:
                  "linear-gradient(135deg, rgba(2,136,209,0.05), rgba(14,165,233,0.03))",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    background: "linear-gradient(135deg, #059669, #10b981)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 10px rgba(5,150,105,0.3)",
                  }}
                >
                  <FaUserGraduate size={14} color="#fff" />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: "#0c1a2e",
                      fontSize: "0.92rem",
                    }}
                  >
                    Students
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    {subjectStudents.length} enrolled · click a student to
                    enter students  scores
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                {subjectStudents.map((student) => {
                  const isExpanded = expandedStudentId === student.id;
                  // const scores = studentScores[student.id] || [];
                  const scoredItems = items.filter(
                    (item) => getScore(student.id, item.id) !== "N/A"
                  );

                  return (
                    <Paper
                      key={student.id}
                      elevation={0}
                      sx={{
                        borderRadius: 2.5,
                        border: isExpanded
                          ? "1px solid rgba(2,136,209,0.4)"
                          : "1px solid #e2e8f0",
                        background: isExpanded
                          ? "rgba(2,136,209,0.02)"
                          : "#f8fafc",
                        overflow: "hidden",
                        transition: "all 0.25s",
                        boxShadow: isExpanded
                          ? "0 4px 20px rgba(2,136,209,0.12)"
                          : "none",
                      }}
                    >
                      {/* Student row */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 2, py: 1.5, cursor: "pointer" }}
                        onClick={() => handleToggleStudent(student.id)}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                        >
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: "50%",
                              background: isExpanded
                                ? "linear-gradient(135deg, #0288D1, #0ea5e9)"
                                : "linear-gradient(135deg, rgba(2,136,209,0.12), rgba(14,165,233,0.18))",
                              border: "1px solid rgba(2,136,209,0.2)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              boxShadow: isExpanded
                                ? "0 4px 10px rgba(2,136,209,0.3)"
                                : "none",
                              transition: "all 0.25s",
                            }}
                          >
                            <FaUserGraduate
                              size={15}
                              color={isExpanded ? "#fff" : "#0288D1"}
                            />
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: "#0c1a2e",
                                textTransform: "uppercase",
                                fontSize: "0.85rem",
                                letterSpacing: "0.4px",
                              }}
                            >
                              {student.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#94a3b8" }}
                            >
                              {scoredItems.length} of {items.length} component
                              scored
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={1}>
                          {scoredItems.length > 0 && (
                            <Chip
                              label={`${scoredItems.length} scored`}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                background: "rgba(5,150,105,0.1)",
                                color: "#059669",
                                border: "1px solid rgba(5,150,105,0.2)",
                              }}
                            />
                          )}
                          <Box
                            sx={{
                              color: "#0288D1",
                              transition: "transform 0.25s",
                              transform: isExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                            }}
                          >
                            <FaChevronDown size={13} />
                          </Box>
                        </Stack>
                      </Stack>

                      {/* Expanded content */}
                      {isExpanded && (
                        <Box sx={{ borderTop: "1px solid #e2e8f0" }}>
                          {loadingStudentScores &&
                            !studentScores[student.id] && (
                              <Stack alignItems="center" py={3}>
                                <CircularProgress
                                  size={24}
                                  sx={{ color: "#0288D1" }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#64748b", mt: 1 }}
                                >
                                  Loading scores...
                                </Typography>
                              </Stack>
                            )}

                          <Box sx={{ p: 2.5 }}>
                            <Grid container spacing={2.5}>
                              {/* Assign score */}
                              <Grid item xs={12} sm={5}>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    background: "rgba(2,136,209,0.04)",
                                    border: "1px solid rgba(2,136,209,0.14)",
                                    height: "100%",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "0.68rem",
                                      fontWeight: 700,
                                      color: "#0288D1",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.8px",
                                      mb: 1.5,
                                    }}
                                  >
                                    🎯 Assign Score
                                  </Typography>
                                  <Stack spacing={1.5}>
                                    <Select
                                      value={newScore.activity_item_id}
                                      onChange={(e) =>
                                        setNewScore({
                                          ...newScore,
                                          activity_item_id: e.target.value,
                                        })
                                      }
                                      displayEmpty
                                      size="small"
                                      fullWidth
                                      sx={selectSx}
                                    >
                                      <MenuItem
                                        value=""
                                        disabled
                                        sx={{ color: "#94a3b8" }}
                                      >
                                        Select Assessment Component
                                      </MenuItem>
                                      {items.map((item) => (
                                        <MenuItem key={item.id} value={item.id}>
                                          {item.title}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    <TextField
                                      placeholder="Enter score obtained"
                                      type="number"
                                      value={newScore.score}
                                      onChange={(e) =>
                                        setNewScore({
                                          ...newScore,
                                          score: e.target.value,
                                        })
                                      }
                                      size="small"
                                      fullWidth
                                      sx={inputSx}
                                    />
                                    <Button
                                      onClick={() =>
                                        handleCreateScore(student.id)
                                      }
                                      disabled={loadingScore}
                                      variant="contained"
                                      fullWidth
                                      startIcon={
                                        loadingScore ? null : (
                                          <FaPlus size={11} />
                                        )
                                      }
                                      sx={primaryBtnSx}
                                    >
                                      {loadingScore ? (
                                        <CircularProgress
                                          size={16}
                                          sx={{ color: "#fff" }}
                                        />
                                      ) : editingScore ? (
                                        "Update Score"
                                      ) : (
                                        "Add Score"
                                      )}
                                    </Button>
                                    {editingScore && (
                                      <Button
                                        color="error"
                                        onClick={() => {
                                          setEditingScore(null);
                                          setNewScore({
                                            activity_item_id: "",
                                            score: "",
                                          });
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    )}
                                  </Stack>

                                  {/* Existing scores */}
                                  {scoredItems.length > 0 && (
                                    <Box mt={2}>
                                      <Divider sx={{ mb: 1.5 }} />
                                      <Typography
                                        sx={{
                                          fontSize: "0.68rem",
                                          fontWeight: 700,
                                          color: "#64748b",
                                          textTransform: "uppercase",
                                          letterSpacing: "0.8px",
                                          mb: 1,
                                        }}
                                      >
                                        Existing Scores
                                      </Typography>
                                      <Stack spacing={1}>
                                        {scoredItems.map((item) => (
                                          <Paper
                                            key={item.id}
                                            elevation={0}
                                            sx={{
                                              px: 1.5,
                                              py: 1,
                                              borderRadius: 1.5,
                                              background: "#fff",
                                              border: "1px solid #e2e8f0",
                                            }}
                                          >
                                            <Stack
                                              direction="row"
                                              alignItems="center"
                                              justifyContent="space-between"
                                            >
                                              <Box>
                                                <Typography
                                                  sx={{
                                                    fontSize: "0.78rem",
                                                    fontWeight: 600,
                                                    color: "#0c1a2e",
                                                  }}
                                                >
                                                  {item.title}
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: "#059669",
                                                    fontWeight: 700,
                                                  }}
                                                >
                                                  {getScore(
                                                    student.id,
                                                    item.id
                                                  )}{" "}
                                                  / {item.total_mark}
                                                </Typography>
                                              </Box>
                                              <Stack
                                                direction="row"
                                                spacing={0.5}
                                              >
                                                <Tooltip
                                                  title="Edit score"
                                                  arrow
                                                >
                                                  <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                      handleEditScore(
                                                        item.id,
                                                        student.id
                                                      )
                                                    }
                                                    disabled={loadingScore}
                                                    sx={editIconSx}
                                                  >
                                                    {editingScore ===
                                                    item.id ? (
                                                      <CircularProgress
                                                        size={10}
                                                        sx={{
                                                          color: "#0288D1",
                                                        }}
                                                      />
                                                    ) : (
                                                      <FaEdit size={11} />
                                                    )}
                                                  </IconButton>
                                                </Tooltip>
                                                <Tooltip
                                                  title="Delete score"
                                                  arrow
                                                >
                                                  <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                      handleDeleteScore(
                                                        item.id,
                                                        student.id
                                                      )
                                                    }
                                                    disabled={
                                                      deletingScoreId ===
                                                      item.id
                                                    }
                                                    sx={deleteIconSx}
                                                  >
                                                    {deletingScoreId ===
                                                    item.id ? (
                                                      <CircularProgress
                                                        size={10}
                                                        sx={{
                                                          color: "#ef4444",
                                                        }}
                                                      />
                                                    ) : (
                                                      <FaTrash size={11} />
                                                    )}
                                                  </IconButton>
                                                </Tooltip>
                                              </Stack>
                                            </Stack>
                                          </Paper>
                                        ))}
                                      </Stack>
                                    </Box>
                                  )}
                                </Paper>
                              </Grid>

                              {/* Activity breakdown table */}
                              <Grid item xs={12} sm={7}>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    borderRadius: 2,
                                    background: "#fff",
                                    border: "1px solid #e2e8f0",
                                    overflow: "hidden",
                                    height: "100%",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      px: 2,
                                      py: 1.5,
                                      borderBottom: "1px solid #e2e8f0",
                                      background: "#f8fafc",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "0.68rem",
                                        fontWeight: 700,
                                        color: "#64748b",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.8px",
                                      }}
                                    >
                                      📊 Activity Breakdown
                                    </Typography>
                                  </Box>
                                  <Box sx={{ overflowX: "auto" }}>
                                    <table
                                      style={{
                                        width: "100%",
                                        borderCollapse: "collapse",
                                      }}
                                    >
                                      <thead>
                                        <tr style={{ background: "#f8fafc" }}>
                                          {[
                                            "Type",
                                            "Item",
                                            "Total",
                                            "Score",
                                          ].map((h) => (
                                            <th
                                              key={h}
                                              style={{
                                                padding: "10px 14px",
                                                textAlign: "left",
                                                fontSize: "0.68rem",
                                                fontWeight: 700,
                                                color: "#64748b",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.6px",
                                                borderBottom:
                                                  "1px solid #e2e8f0",
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              {h}
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {activityTypes.map((type) =>
                                          items
                                            .filter(
                                              (item) =>
                                                item.activity_type === type.id
                                            )
                                            .map((item, idx) => {
                                              const score = getScore(
                                                student.id,
                                                item.id
                                              );
                                              const hasScore = score !== "N/A";
                                              return (
                                                <tr
                                                  key={item.id}
                                                  style={{
                                                    background:
                                                      idx % 2 === 0
                                                        ? "#fff"
                                                        : "#f8fafc",
                                                    transition:
                                                      "background 0.15s",
                                                  }}
                                                >
                                                  <td
                                                    style={{
                                                      padding: "10px 14px",
                                                      borderBottom:
                                                        "1px solid #f1f5f9",
                                                    }}
                                                  >
                                                    <Stack
                                                      direction="row"
                                                      alignItems="center"
                                                      spacing={0.5}
                                                    >
                                                      <Typography
                                                        sx={{
                                                          fontSize: "0.78rem",
                                                          fontWeight: 600,
                                                          color: "#0c1a2e",
                                                        }}
                                                      >
                                                        {type.name}
                                                      </Typography>
                                                      <Chip
                                                        label={`${type.weight}%`}
                                                        size="small"
                                                        sx={{
                                                          height: 16,
                                                          fontSize: "0.6rem",
                                                          fontWeight: 700,
                                                          background:
                                                            Number(
                                                              type.weight
                                                            ) === 0
                                                              ? "rgba(239,68,68,0.1)"
                                                              : "rgba(2,136,209,0.1)",
                                                          color:
                                                            Number(
                                                              type.weight
                                                            ) === 0
                                                              ? "#dc2626"
                                                              : "#0288D1",
                                                          border: "none",
                                                        }}
                                                      />
                                                    </Stack>
                                                  </td>
                                                  <td
                                                    style={{
                                                      padding: "10px 14px",
                                                      borderBottom:
                                                        "1px solid #f1f5f9",
                                                    }}
                                                  >
                                                    <Typography
                                                      sx={{
                                                        fontSize: "0.78rem",
                                                        color: "#334155",
                                                      }}
                                                    >
                                                      {item.title}
                                                    </Typography>
                                                  </td>
                                                  <td
                                                    style={{
                                                      padding: "10px 14px",
                                                      borderBottom:
                                                        "1px solid #f1f5f9",
                                                    }}
                                                  >
                                                    <Typography
                                                      sx={{
                                                        fontSize: "0.78rem",
                                                        fontWeight: 600,
                                                        color: "#64748b",
                                                      }}
                                                    >
                                                      {item.total_mark}
                                                    </Typography>
                                                  </td>
                                                  <td
                                                    style={{
                                                      padding: "10px 14px",
                                                      borderBottom:
                                                        "1px solid #f1f5f9",
                                                    }}
                                                  >
                                                    <Chip
                                                      label={
                                                        hasScore ? score : "—"
                                                      }
                                                      size="small"
                                                      sx={{
                                                        height: 20,
                                                        fontSize: "0.72rem",
                                                        fontWeight: 700,
                                                        background: hasScore
                                                          ? "rgba(5,150,105,0.1)"
                                                          : "#f1f5f9",
                                                        color: hasScore
                                                          ? "#059669"
                                                          : "#94a3b8",
                                                        border: `1px solid ${
                                                          hasScore
                                                            ? "rgba(5,150,105,0.25)"
                                                            : "#e2e8f0"
                                                        }`,
                                                      }}
                                                    />
                                                  </td>
                                                </tr>
                                              );
                                            })
                                        )}
                                      </tbody>
                                    </table>
                                  </Box>
                                </Paper>
                              </Grid>
                            </Grid>
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ActivityPage;
